import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
    getFirestore, 
    doc, 
    setDoc, 
    getDoc, 
    collection, 
    query, 
    onSnapshot, 
    addDoc,
    deleteDoc,
    getDocs,
    where,
    orderBy,
    updateDoc
} from 'firebase/firestore';

const AdminCalendar = ({ user, db, setModalMessage, userColor, appId, onShowEventModal }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [calendarView, setCalendarView] = useState('month');
    const [isAnimating, setIsAnimating] = useState(false);
    const [hoveredDay, setHoveredDay] = useState(null);

    // Fetch events from Firestore
    useEffect(() => {
        if (!user || !db || !appId) return;

        const eventsCollectionRef = collection(db, `artifacts/${appId}/public/data/calendarEvents`);
        const q = query(eventsCollectionRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedEvents = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    startDate: data.startDate,
                    endDate: data.endDate,
                    dateObj: new Date(data.startDate)
                };
            });
            setEvents(fetchedEvents);
        }, (error) => {
            console.error("Error fetching calendar events:", error);
            setModalMessage(`Error loading calendar events: ${error.message}`);
        });

        return () => unsubscribe();
    }, [user, db, setModalMessage, appId]);

    const handleSaveEvent = async (eventData) => {
        if (!user || !db || !appId) {
            setModalMessage("User not authenticated, database not available, or app ID missing.");
            return;
        }

        try {
            if (eventData.id) {
                // Update existing event
                const eventDocRef = doc(db, `artifacts/${appId}/public/data/calendarEvents`, eventData.id);
                await updateDoc(eventDocRef, {
                    title: eventData.title,
                    description: eventData.description,
                    allDay: eventData.allDay,
                    startDate: eventData.startDate,
                    endDate: eventData.endDate,
                    startTime: eventData.startTime,
                    endTime: eventData.endTime,
                    notes: eventData.notes,
                    color: eventData.color,
                    updatedAt: new Date().toISOString(),
                    updatedBy: user.uid
                });
                setModalMessage("Event updated successfully!");
            } else {
                // Create new event
                await addDoc(collection(db, `artifacts/${appId}/public/data/calendarEvents`), {
                    title: eventData.title,
                    description: eventData.description,
                    allDay: eventData.allDay,
                    startDate: eventData.startDate,
                    endDate: eventData.endDate,
                    startTime: eventData.startTime,
                    endTime: eventData.endTime,
                    notes: eventData.notes,
                    color: eventData.color,
                    createdAt: new Date().toISOString(),
                    createdBy: user.uid
                });
                setModalMessage("Event created successfully!");
            }
        } catch (error) {
            console.error("Error saving event:", error);
            setModalMessage(`Failed to save event: ${error.message}`);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!user || !db || !appId) {
            setModalMessage("User not authenticated, database not available, or app ID missing.");
            return;
        }
        try {
            const eventDocRef = doc(db, `artifacts/${appId}/public/data/calendarEvents`, eventId);
            await deleteDoc(eventDocRef);
            setModalMessage("Event deleted successfully!");
        } catch (error) {
            console.error("Error deleting event:", error);
            setModalMessage(`Failed to delete event: ${error.message}`);
        }
    };

    const openAddEventModal = (date) => {
        setSelectedDate(date);
        onShowEventModal(null); // null for new event
    };

    const openEditEventModal = (event) => {
        setSelectedDate(event.dateObj);
        onShowEventModal(event); // pass event data for editing
    };

    const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const days = [];
    const numDays = daysInMonth(currentMonth);
    const startDay = firstDayOfMonth(currentMonth);

    // Fill leading empty days with prev month dates
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startDay - 1; i >= 0; i--) {
        const day = prevMonthDays - i;
        days.push(
            <div key={`empty-start-${i}`} className="p-2 text-center text-gray-600 hover:bg-white/5 transition-all duration-300 rounded-xl relative group animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                <span className="text-sm opacity-50 group-hover:opacity-70 transition-opacity">{day}</span>
            </div>
        );
    }

    // Fill days of the month
    for (let i = 1; i <= numDays; i++) {
        const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
        const eventsOnDay = events.filter(event => 
            event.dateObj.toDateString() === dayDate.toDateString()
        );
        const isSelected = selectedDate && selectedDate.toDateString() === dayDate.toDateString();
        const isToday = dayDate.toDateString() === new Date().toDateString();
        const isHovered = hoveredDay === i;

        days.push(
            <div 
                key={`day-${i}`} 
                className={`relative p-3 text-center rounded-2xl transition-all duration-500 cursor-pointer group min-h-[80px] flex flex-col justify-between items-center animate-slide-up ${
                    isToday 
                    ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-400/50 shadow-lg shadow-blue-500/25' 
                    : 'glass-card border border-white/10 hover:border-cyan-400/30'
                } ${
                    isSelected 
                    ? 'ring-2 ring-cyan-400 bg-gradient-to-br from-cyan-500/10 to-blue-500/10' 
                    : ''
                } hover:scale-105 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/20`}
                style={{ animationDelay: `${i * 30}ms` }}
                onClick={() => setSelectedDate(dayDate)}
                onMouseEnter={() => setHoveredDay(i)}
                onMouseLeave={() => setHoveredDay(null)}
            >
                {/* Background Animation */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Date Number */}
                <div className="relative z-10 w-full flex justify-between items-start">
                    <span className={`text-lg font-bold transition-all duration-300 ${
                        isToday 
                        ? 'text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text' 
                        : isSelected 
                        ? 'text-cyan-400' 
                        : 'text-white group-hover:text-cyan-300'
                    }`}>
                        {i}
                    </span>
                    {isToday && (
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-pulse"></div>
                    )}
                </div>

                {/* Events Display */}
                {eventsOnDay.length > 0 && (
                    <div className="relative z-10 w-full mt-2">
                        <div className="flex flex-wrap justify-center gap-1 mb-1">
                            {eventsOnDay.slice(0, 3).map((event, idx) => (
                                <div 
                                    key={idx} 
                                    className="w-2 h-2 rounded-full animate-bounce-slow shadow-sm"
                                    style={{ 
                                        backgroundColor: event.color || userColor || '#4d9eff',
                                        animationDelay: `${idx * 200}ms`
                                    }}
                                    title={event.title}
                                ></div>
                            ))}
                        </div>
                        {eventsOnDay.length > 3 && (
                            <span className="text-xs text-cyan-400 font-semibold animate-pulse">+{eventsOnDay.length - 3} more</span>
                        )}
                    </div>
                )}

                {/* Add Event Icon (appears on hover) */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                    <button 
                        className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-200"
                        onClick={(e) => {
                            e.stopPropagation();
                            openAddEventModal(dayDate);
                        }}
                    >
                        <i className="fas fa-plus text-white text-sm"></i>
                    </button>
                </div>
            </div>
        );
    }

    const eventsForSelectedDate = selectedDate 
        ? events.filter(event => event.dateObj.toDateString() === selectedDate.toDateString())
        : [];

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-blue-900/20 to-purple-900/20 p-6 rounded-3xl">
            {/* Floating Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none ">
                <div className="absolute -top-1/2 -right-1/2 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-float"></div>
                <div className="absolute -bottom-1/2 -left-1/2 w-96 h-96 bg-gradient-to-tr from-cyan-500/10 to-pink-500/10 rounded-full blur-3xl animate-bounce-slow"></div>
                <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-full blur-2xl animate-pulse"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="glass-card rounded-3xl p-8 mb-8 border border-white/20 backdrop-blur-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-6">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity animate-pulse"></div>
                                <div className="relative w-20 h-20 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-3xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                                    <i className="fas fa-calendar-alt text-4xl text-white animate-float"></i>
                                </div>
                            </div>
                            <div>
                                <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
                                    Calendar
                                </h1>
                                <p className="text-gray-300 text-xl mt-2 animate-slide-in">
                                    Manage your events and schedule
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                            <button 
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                                className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                            >
                                <i className="fas fa-chevron-left text-gray-300"></i>
                            </button>
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-white">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </h2>
                            </div>
                            <button 
                                onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                                className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                            >
                                <i className="fas fa-chevron-right text-gray-300"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Calendar Grid */}
                <div className="glass-card rounded-3xl p-8 border border-white/20 backdrop-blur-lg">
                    {/* Days of Week Header */}
                    <div className="grid grid-cols-7 gap-4 mb-6">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                            <div key={day} className="text-center py-4 text-gray-300 font-bold text-lg">
                                <div className="relative">
                                    <span className="relative z-10">{day}</span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-4">
                        {days}
                    </div>
                </div>

                {/* Selected Date Events */}
                {selectedDate && eventsForSelectedDate.length > 0 && (
                    <div className="glass-card rounded-3xl p-8 mt-8 border border-white/20 backdrop-blur-lg animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-white flex items-center">
                                <i className="fas fa-calendar-day mr-3 text-cyan-400"></i>
                                Events for {selectedDate.toLocaleDateString()}
                            </h3>
                            <button
                                onClick={() => openAddEventModal(selectedDate)}
                                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                            >
                                <i className="fas fa-plus mr-2"></i>
                                Add Event
                            </button>
                        </div>
                        <div className="space-y-4">
                            {eventsForSelectedDate.map((event, index) => (
                                <div 
                                    key={event.id} 
                                    className="glass-card rounded-2xl p-6 border border-white/10 hover:border-cyan-400/30 transition-all duration-300 group animate-slide-in"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div 
                                                className="w-4 h-4 rounded-full animate-pulse"
                                                style={{ backgroundColor: event.color || userColor || '#4d9eff' }}
                                            ></div>
                                            <div>
                                                <h4 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                                                    {event.title}
                                                </h4>
                                                {event.description && (
                                                    <p className="text-gray-400 mt-1">{event.description}</p>
                                                )}
                                                <div className="flex items-center mt-2 text-sm text-gray-400">
                                                    {event.allDay ? (
                                                        <span className="flex items-center">
                                                            <i className="fas fa-sun mr-1"></i>
                                                            All Day
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center">
                                                            <i className="fas fa-clock mr-1"></i>
                                                            {event.startTime} - {event.endTime}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openEditEventModal(event)}
                                            className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100"
                                        >
                                            <i className="fas fa-edit text-gray-300"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminCalendar;
